/**
 * Authentication Service
 * Handles secure authentication with JWT tokens stored in HTTP-only cookies
 */

import { api } from './api';

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: string;
  organizationId: string;
  permissions: string[];
  preferredLanguage: 'en' | 'ar';
}

export interface LoginResponse {
  user: User;
  requireMFA: boolean;
}

class AuthService {
  private readonly STORAGE_KEY = 'omni_user';
  private readonly TOKEN_KEY = 'omni_token';
  private readonly REFRESH_KEY = 'omni_refresh';

  /**
   * Login with email and password
   * Tokens are stored in HTTP-only cookies by the backend
   */
  async login(email: string, password: string, mfaCode?: string): Promise<LoginResponse> {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
        mfaCode,
      });

      if (response.requireMFA) {
        return {
          user: null as any,
          requireMFA: true,
        };
      }

      // Store user info in localStorage (NOT the tokens!)
      // Tokens are in HTTP-only cookies
      this.setUser(response.user);

      return {
        user: response.user,
        requireMFA: false,
      };
    } catch (error: any) {
      // Handle 401 specifically for login - wrong credentials
      if (error.response?.status === 401) {
        throw new Error('Wrong email or password');
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      this.clearUser();
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<void> {
    await api.post('/auth/refresh');
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): User | null {
    const userJson = localStorage.getItem(this.STORAGE_KEY);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  /**
   * Store user in localStorage
   */
  private setUser(user: User): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
  }

  /**
   * Clear user from storage
   */
  private clearUser(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_KEY);
  }

  /**
   * Set tokens (for SSO callback)
   */
  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_KEY, refreshToken);
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_KEY);
  }
}

export const authService = new AuthService();
